import React, { useState } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useReduceEffects } from '../../../hooks/useReduceEffects.js';
import { useFxPreview } from '../../../hooks/useFxPreview.js';
import { FX_ITEM_KEY } from '../../../_helpers/fxItems.js';
import { canBuyShopItem } from '../../../_helpers/skinShop.js';
import { skinPurchaseError } from './SkinTryOnPanel.jsx';
import SkinPriceTag, { skinPriceLabel } from './SkinPriceTag.jsx';
import SkinTimeLimit, { useOfferClocks } from './SkinTimeLimit.jsx';
import PhotosensitivityToggle from './PhotosensitivityToggle.jsx';
import SkinAvailability from './SkinAvailability.jsx';
import { ShopRarityTag } from '../shop/ShopPreview.jsx';
import '../../../styles/SkinTryOnPanel.css';
import '../../../styles/FxTryOnPanel.css';

/**
 * The live preview itself: a sample album card (and the card it passes) on a
 * stage of its own (useFxPreview). Never the stream page's queue.
 */
export const FxPreview = ({ item, timing, showReplay = true }) => {
  const { t } = useLocale();
  const reduceEffects = useReduceEffects();
  const preview = useFxPreview({
    itemType: item.skin.type,
    packageId: item.skin.fxPackageId,
    suggestedBoostId: item.skin.defaultBoostPackageId ?? null,
    reduceEffects,
    ...(timing ? { timing } : {}),
  });
  const { status } = preview;

  return (
    <div className="fx-preview" data-testid="fx-preview" data-status={status}>
      <div ref={preview.containerRef} className="fx-preview-stage" data-testid="fx-preview-stage">
        <div ref={preview.rowRef} className="fx-preview-row ssfx-row">
          <div ref={preview.neighbourRef} className="fx-preview-card fx-preview-card--neighbour ssfx-card" aria-hidden="true">
            <div className="fx-preview-cover fx-preview-cover--neighbour" />
          </div>
          <div ref={preview.cardRef} className="fx-preview-card ssfx-card" aria-hidden="true">
            <div ref={preview.innerRef} className="fx-preview-inner">
              <div ref={preview.coverRef} className="fx-preview-cover" />
              <span className="fx-preview-song">{t('shop.sampleSong.title')}</span>
              <span className="fx-preview-artist">{t('shop.sampleSong.artist')}</span>
            </div>
          </div>
        </div>
      </div>
      <p className="fx-preview-status" role="status" data-testid="fx-preview-status">
        {status === 'loading' && t('stream.fxItem.preview.loading')}
        {status === 'unsupported' && t('stream.fxItem.preview.unsupported')}
        {status === 'failed' && t('stream.fxItem.preview.failed')}
      </p>
      {showReplay && (status === 'done' || status === 'failed') && (
        <button type="button" className="skin-try-on-action fx-preview-replay" onClick={preview.replay} data-testid="fx-preview-replay">
          {t('stream.fxItem.preview.replay')}
        </button>
      )}
    </div>
  );
};

/**
 * Trying on a vibe set or boost animation (ENG-816), on the skin shelf and in
 * the shop: "Previewing Neon Storm", what the item is, a live preview on a
 * sample album card (FxPreview), the "Photosensitivity mode" switch (ENG-836),
 * and Get it and Close preview. The switch shows in the Shop's try-on only:
 * the stream page's skin shelf passes `showPhotosensitivityToggle={false}`
 * (ENG-878). Either way the preview follows the account setting.
 *
 * `item` is a shop item (skinShop.js) or an owned shelf entry shaped like one:
 * `{ skin, priceCents?, soldOut?, owned?, forSale?, editionSize?, ... }`.
 * One not owned shows what's left ("12 left · #489 of 500") or the Sold out
 * badge (SkinAvailability, ENG-833). Get it shows only when
 * the item can be bought (canBuyShopItem); a $0 item reads "Get it free", as
 * the shop does (ENG-827). Every purchase goes through useSkinPurchase:
 * `purchase` is its `{ state, error, priceChange }` and `onGetIt(item)` calls
 * its `buy` with the item's `priceCents` (sent as `expected_price_cents`).
 * On sale (ENG-832) the price is SkinPriceTag's (struck-through regular
 * price, sale price, Sale badge, its end); once the sale ends Get it turns off
 * and `onOfferEnd(skinId)` asks the page to read the item again.
 * A time-limited item (ENG-834) shows when it stops being for sale
 * (SkinTimeLimit), as the shop and try-on do; a sale ending at the same
 * moment shows only the sale's end line. When the window closes on screen,
 * Get it turns off with "No longer available", the page reads the item again
 * (`onOfferEnd`), and the preview stays open with that line and Close preview.
 *
 * `variant="shop"` is the skins shop's look (ENG-894), for its hero banner
 * and its sticky try-on panel: the live preview under the "Previewing …"
 * pill, then "Preview · only you see this", the name and rarity, the type and
 * what's left, what the item is, the price, the time limit, the switch, Get
 * it, Play again (it plays the preview from the start) and Close preview.
 * Only the look changes; what each part does is the same.
 */
const FxTryOnPanel = ({ item, purchase = null, onGetIt, onClose, timing = null, onOfferEnd = null, showPhotosensitivityToggle = true, variant = 'stream' }) => {
  const { t, format } = useLocale();
  const [run, setRun] = useState(0);
  const selling = Boolean(item?.skin) && !item.owned && item.forSale !== false;
  const saleItem = selling ? item.sale ?? null : null;
  const until = selling ? item.availableUntil ?? null : null;
  const clocks = useOfferClocks(saleItem, until, () => onOfferEnd?.(item?.skin?.id ?? null));
  if (!item?.skin) return null;
  const { skin } = item;
  const typeKey = FX_ITEM_KEY[skin.type];
  const free = item.priceCents === 0;
  const price = item.priceCents != null && item.forSale !== false ? skinPriceLabel(t, format, item.priceCents) : null;
  const buyable = canBuyShopItem(item);
  const state = purchase?.state ?? 'idle';
  const busy = state === 'buying' || state === 'redirecting';

  let status = null;
  if (state === 'buying') status = t('stream.tryOn.buying');
  else if (state === 'redirecting') status = t('stream.tryOn.redirecting');
  else if (purchase?.error) status = skinPurchaseError(t, format, purchase);
  else if (!item.owned && item.soldOut) status = t('stream.tryOn.soldOut');

  const priceTag = !item.owned && price && (
    <SkinPriceTag
      priceCents={item.priceCents}
      sale={saleItem}
      clock={clocks.saleClock}
      windowEnds={clocks.shared}
      className="fx-try-on-price"
      testId="fx-try-on-price"
    />
  );
  const timeLimit = !clocks.shared && !item.soldOut && (
    <SkinTimeLimit until={until} clock={clocks.windowClock} testId="fx-try-on-time-limit" />
  );
  const statusLine = status && (
    <p className="skin-try-on-status" role="status" data-testid="fx-try-on-status">{status}</p>
  );
  const getItButton = buyable && (
    <button
      type="button"
      className="skin-try-on-action skin-try-on-action--primary"
      onClick={() => onGetIt?.(item)}
      disabled={busy || clocks.ended}
      aria-label={free ? t('shop.getItFreeLabel', { name: skin.name }) : t('shop.getItLabel', { name: skin.name, price })}
      data-testid="fx-try-on-get"
    >
      {free ? t('shop.getItFree') : t('shop.getIt', { price })}
    </button>
  );
  const closeButton = (
    <button type="button" className="skin-try-on-action" onClick={onClose} disabled={state === 'redirecting'} data-testid="fx-try-on-close">
      {t('stream.fxItem.preview.close')}
    </button>
  );

  if (variant === 'shop') {
    return (
      <section className="skin-try-on skin-try-on--shop fx-try-on" data-testid="fx-try-on" aria-label={t('stream.fxItem.preview.label', { name: skin.name })}>
        <div className="skin-try-on-stage">
          <span className="skin-try-on-pill" data-testid="fx-try-on-pill">{t('stream.fxItem.preview.label', { name: skin.name })}</span>
          <FxPreview key={`${skin.id}:${skin.fxPackageId}:${run}`} item={item} timing={timing} showReplay={false} />
        </div>
        <div className="skin-try-on-info">
          <p className="skin-try-on-kicker">{t('shop.look.previewing')}</p>
          <div className="skin-try-on-name-row">
            <h2 className="skin-try-on-name">{skin.name}</h2>
            <ShopRarityTag rarity={skin.rarity} />
          </div>
          <p className="skin-try-on-type">
            <span className="fx-try-on-type" data-testid="fx-try-on-type">{t(`stream.fxItem.type.${typeKey}`)}</span>
            <SkinAvailability offer={item} owned={item.owned === true} />
          </p>
          {priceTag}
          {timeLimit}
          <p className="skin-try-on-body">{t(`stream.fxItem.preview.about.${typeKey}`)}</p>
          {showPhotosensitivityToggle && <PhotosensitivityToggle variant="stream" />}
          {statusLine}
          <div className="skin-try-on-actions">
            {getItButton}
            <button type="button" className="skin-try-on-action" onClick={() => setRun((n) => n + 1)} data-testid="fx-try-on-replay">
              {t('stream.fxItem.preview.replay')}
            </button>
            {closeButton}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="skin-try-on fx-try-on" data-testid="fx-try-on" aria-label={t('stream.fxItem.preview.label', { name: skin.name })}>
      <div className="skin-try-on-text">
        <h3 className="skin-try-on-title">{t('stream.fxItem.preview.title', { name: skin.name })}</h3>
        <span className="skin-try-on-tag fx-try-on-type" data-testid="fx-try-on-type">{t(`stream.fxItem.type.${typeKey}`)}</span>
        <p className="skin-try-on-body">{t(`stream.fxItem.preview.about.${typeKey}`)}</p>
        {priceTag}
        {timeLimit}
        <SkinAvailability offer={item} owned={item.owned === true} />
      </div>
      <FxPreview key={`${skin.id}:${skin.fxPackageId}`} item={item} timing={timing} />
      {showPhotosensitivityToggle && <PhotosensitivityToggle variant="stream" />}
      {statusLine}
      <div className="skin-try-on-actions">
        {getItButton}
        {closeButton}
      </div>
    </section>
  );
};

export default FxTryOnPanel;
